import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CheckCircle, XCircle, PencilSimple, Trash, Users, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [refreshTick, setRefreshTick] = useState(0);

    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resetInfo, setResetInfo] = useState(null);
    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        position: "",
        pay_rate: "0",
        active: true,
        approval_status: "pending",
        date_of_birth: "",
        include_in_attendance_recognition: true,
        permissions: {},
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (filterStatus !== "all") params.status = filterStatus;
                const { data } = await api.get("/users", { params });
                if (!cancelled) setUsers(data || []);
            } catch {
                if (!cancelled) toast.error("Failed to load users");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filterStatus, refreshTick]);

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    const approve = async (id) => {
        try {
            await api.patch(`/users/${id}`, { approval_status: "approved", active: true });
            toast.success("Approved");
            reload();
        } catch {
            toast.error("Failed to approve");
        }
    };

    const reject = async (id) => {
        if (!window.confirm("Reject this account? The user will not be able to sign in.")) return;
        try {
            await api.patch(`/users/${id}`, { approval_status: "rejected" });
            toast.success("Rejected");
            reload();
        } catch {
            toast.error("Failed to reject");
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "Deactivated" : "Activated");
            reload();
        } catch {
            toast.error("Failed to update");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this user permanently? All their reports and requests will also be removed.")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success("Deleted");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete");
        }
    };

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            full_name: u.full_name || "",
            phone: u.phone || "",
            position: u.position || "",
            pay_rate: String(u.pay_rate ?? 0),
            active: !!u.active,
            approval_status: u.approval_status,
            date_of_birth: u.date_of_birth || "",
            include_in_attendance_recognition: u.include_in_attendance_recognition !== false,
            permissions: Object.fromEntries(
                ["can_publish_announcements","can_manage_announcements","can_delete_announcements","can_view_announcement_tracking"].map((k) => [k, Boolean((u.permissions || {})[k] ?? u.role === "admin")]),
            ),
        });
        setEditOpen(true);
    };

    const saveEdit = async () => {
        try {
            await api.patch(`/users/${editing.id}`, {
                full_name: form.full_name,
                phone: form.phone,
                position: form.position,
                pay_rate: Number(form.pay_rate || 0),
                active: form.active,
                approval_status: form.approval_status,
                date_of_birth: form.date_of_birth,
                include_in_attendance_recognition: form.include_in_attendance_recognition,
                permissions: form.permissions,
            });
            toast.success("Updated");
            setEditOpen(false);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update");
        }
    };

    const createResetLink = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.id}/reset-link`);
            setResetInfo(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to generate reset link");
        }
    };


    return (
        <div className="space-y-6">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Admin · Team</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Technicians
                </h1>
                <p className="text-slate-600 mt-2 text-sm">
                    Approve new accounts, manage profiles, and set pay rates.
                </p>
            </div>

            <Card className="border-[#E2E8F0]">
                <CardContent className="p-4 sm:p-5 flex items-center gap-3">
                    <div className="flex-1 max-w-xs">
                        <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                            Filter by status
                        </Label>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="h-10 mt-1" data-testid="filter-user-status-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table className="min-w-[760px]">
                            <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Position</TableHead>
                                    <TableHead className="hidden lg:table-cell">Date of birth</TableHead>
                                    <TableHead className="text-right">Pay rate</TableHead>
                                    <TableHead>Approval</TableHead>
                                    <TableHead>Active</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                                            <Users size={32} className="mx-auto mb-3 text-slate-300" weight="duotone" />
                                            <div>No users found.</div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((u) => (
                                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                            <TableCell className="font-medium text-[#1F2A44]">
                                                {u.full_name}
                                                {u.role === "admin" && (
                                                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[#0066D9] font-semibold">
                                                        Admin
                                                    </span>
                                                )}
                                                {u.include_in_attendance_recognition === false && (
                                                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold"
                                                        title="Excluded from attendance rankings and recognition"
                                                        data-testid={`user-not-ranked-${u.id}`}>
                                                        Not ranked
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm">{u.email}</TableCell>
                                            <TableCell className="text-sm">{u.position || "—"}</TableCell>
                                            <TableCell className="hidden lg:table-cell text-sm font-mono text-slate-600"
                                                data-testid={`user-dob-${u.id}`}>
                                                {u.date_of_birth || <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-right">${Number(u.pay_rate || 0).toFixed(2)}</TableCell>
                                            <TableCell>
                                                <StatusBadge status={u.approval_status} />
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge status={u.active ? "active" : "inactive"} />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {u.approval_status === "pending" && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                                onClick={() => approve(u.id)}
                                                                data-testid={`approve-user-${u.id}`}
                                                            >
                                                                <CheckCircle size={14} className="mr-1" /> Approve
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 border-red-300 text-red-700 hover:bg-red-50"
                                                                onClick={() => reject(u.id)}
                                                                data-testid={`reject-user-${u.id}`}
                                                            >
                                                                <XCircle size={14} className="mr-1" /> Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                    {u.role !== "admin" && u.approval_status !== "pending" && (
                                                        <Switch
                                                            checked={u.active}
                                                            onCheckedChange={() => toggleActive(u)}
                                                            data-testid={`toggle-active-${u.id}`}
                                                            aria-label={u.active ? "Deactivate" : "Activate"}
                                                            title={u.active ? "Deactivate" : "Activate"}
                                                        />
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 px-2"
                                                        onClick={() => openEdit(u)}
                                                        data-testid={`edit-user-${u.id}`}
                                                    >
                                                        <PencilSimple size={14} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 px-2 text-slate-500 hover:text-[#0066D9]"
                                                        onClick={() => createResetLink(u)}
                                                        title="Generate password reset link"
                                                        data-testid={`reset-link-user-${u.id}`}
                                                    >
                                                        <Key size={14} />
                                                    </Button>
                                                    {u.role !== "admin" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-8 px-2 text-[#E6002C] hover:text-[#E6002C] hover:bg-red-50"
                                                            onClick={() => remove(u.id)}
                                                            data-testid={`delete-user-${u.id}`}
                                                        >
                                                            <Trash size={14} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Password reset link (admin-generated) */}
            <Dialog open={Boolean(resetInfo)} onOpenChange={(o) => { if (!o) setResetInfo(null); }}>
                <DialogContent className="sm:max-w-lg" data-testid="reset-link-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Password reset link
                        </DialogTitle>
                        <DialogDescription>
                            Single-use recovery link.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-1">
                        <p className="text-sm text-slate-600">
                            Single-use link for <span className="font-medium text-[#1F2A44]">
                                {resetInfo?.employee?.full_name}
                            </span> ({resetInfo?.employee?.email}). It expires in{" "}
                            {resetInfo?.expires_in_minutes} minutes and cancels any earlier link.
                            Share it directly with the employee — never post it publicly.
                        </p>
                        <div className="p-3 rounded-md bg-slate-50 border border-[#E2E8F0] text-xs break-all font-mono text-slate-700"
                            data-testid="reset-link-value">
                            {resetInfo?.reset_link}
                        </div>
                        <Button
                            className="bg-[#003366] hover:bg-[#0066D9]"
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(resetInfo?.reset_link || "");
                                    toast.success("Link copied");
                                } catch {
                                    toast.error("Clipboard blocked — select the link above and copy it manually.");
                                }
                            }}
                            data-testid="copy-reset-link-button"
                        >
                            Copy link
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Edit user
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Full name
                            </Label>
                            <Input
                                value={form.full_name}
                                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                                className="h-11"
                                data-testid="edit-user-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Phone
                            </Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                className="h-11"
                                data-testid="edit-user-phone-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Position
                            </Label>
                            <Input
                                value={form.position}
                                onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                                className="h-11"
                                data-testid="edit-user-position-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Date of birth
                            </Label>
                            <Input
                                type="date"
                                value={form.date_of_birth}
                                onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))}
                                className="h-11"
                                data-testid="edit-user-dob-input"
                            />
                            <p className="text-[11px] text-slate-400">
                                Admin only. The birthday board shows month and day — never the year or age.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Pay rate ($/hr)
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.pay_rate}
                                onChange={(e) => setForm((f) => ({ ...f, pay_rate: e.target.value }))}
                                className="h-11"
                                data-testid="edit-user-pay-rate-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Approval status
                            </Label>
                            <Select
                                value={form.approval_status}
                                onValueChange={(v) => setForm((f) => ({ ...f, approval_status: v }))}
                            >
                                <SelectTrigger className="h-11" data-testid="edit-user-approval-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-3 mt-1">
                            <Switch
                                checked={form.active}
                                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                                data-testid="edit-user-active-switch"
                            />
                            <Label className="text-sm">Active account</Label>
                        </div>
                        <div className="sm:col-span-2 mt-1 pt-3 border-t border-[#E2E8F0] space-y-3">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Announcement permissions
                            </Label>
                            {[["can_publish_announcements", "Publish announcements"],
                              ["can_manage_announcements", "Manage & archive announcements"],
                              ["can_delete_announcements", "Delete announcements"],
                              ["can_view_announcement_tracking", "View announcement tracking"]].map(([key, label]) => (
                                <div key={key} className="flex items-center gap-3">
                                    <Switch
                                        checked={Boolean(form.permissions?.[key])}
                                        onCheckedChange={(v) => setForm((f) => ({
                                            ...f, permissions: { ...(f.permissions || {}), [key]: v },
                                        }))}
                                        data-testid={`edit-user-${key.replace(/_/g, "-")}-switch`}
                                    />
                                    <Label className="text-sm">{label}</Label>
                                </div>
                            ))}
                            <p className="text-xs text-slate-500">
                                Grant these to specific accounts so announcements stay with the people who should send them.
                            </p>
                        </div>
                        <div className="sm:col-span-2 flex items-start gap-3 mt-1 pt-3 border-t border-[#E2E8F0]">
                            <Switch
                                checked={form.include_in_attendance_recognition}
                                onCheckedChange={(v) => setForm((f) => ({ ...f, include_in_attendance_recognition: v }))}
                                data-testid="edit-user-recognition-switch"
                            />
                            <div>
                                <Label className="text-sm">Include in Attendance &amp; Recognition</Label>
                                <p className="text-xs text-slate-500 mt-1">
                                    Off = kept out of every ranking, streak and employee-facing attendance
                                    statistic. Clock In / Clock Out and all history stay untouched.
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={saveEdit}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="edit-user-save-button"
                        >
                            Save changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
